import { type InputHTMLAttributes, forwardRef } from "react";
import { cn } from "../cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {}

/**
 * Single-line text input primitive. Form-agnostic by design: it forwards a
 * ref to the real DOM node and works both controlled and uncontrolled, but it
 * does NOT own a label, error text, or validation state — a future `Field`
 * wrapper (built when the first real multi-field form lands) owns those and
 * wires them up via `aria-describedby`/`aria-invalid`. This keeps the
 * primitive adoptable by any form library (react-hook-form, TanStack Form, …)
 * without call-site churn.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        "h-10 w-full rounded-xl border border-input bg-surface px-3 text-sm text-foreground shadow-sm transition-colors placeholder:text-muted-foreground hover:bg-muted/50 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15 disabled:pointer-events-none disabled:opacity-50 aria-[invalid=true]:border-destructive",
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = "Input";
